import { Component, OnDestroy, OnInit, ElementRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import swal from 'sweetalert2';
import { ConsumerInteractionModel, ConsumerInteractionProgressType, ConsumerInteractionType } from '../../../models/consumer/consumer-interaction.model';
import { ConsumerModel } from '../../../models/consumer/consumer.model';
import { ScreenMessageTemplateModel } from '../../../models/consumer/screen-message-template-list.model';
import { ConsumerInteractionService } from '../../../services/consumer-interaction.service';
import { getInteractionType } from '../../../utilities/consumer-utilities';

@Component({
    selector: 'app-screen-messages-item',
    templateUrl: './screen-messages-item.component.html',
    styles: []
})
export class ScreenMessagesItemComponent implements OnInit, OnDestroy {

    constructor(
        private consumerInteractionService: ConsumerInteractionService,
        private route: ActivatedRoute,
        private fb: UntypedFormBuilder,
        private elRef: ElementRef
    ) { }

    form: UntypedFormGroup;
    clientId: number;
    consumerId: string;
    isSearching: boolean = false;
    showProgress: boolean = false;
    screenMessages: any[] = [];
    selectedTemplateMessage: string;
    messageTemplates: ScreenMessageTemplateModel[];
    screenPage = 1;
    totalPages = 1;
    pageSize = 5;
    messageLength = 0;
    consumer: ConsumerModel = new ConsumerModel();
    subscriptions: any[] = [];
    editMessageToggle: boolean = false;
    progress: ConsumerInteractionProgressType = ConsumerInteractionProgressType.InProgress;
    interactionType: ConsumerInteractionType = ConsumerInteractionType.SendScreenMessage;

    ngOnInit() {
        this.form = this.fb.group({
            'message': [this.selectedTemplateMessage, Validators.compose([Validators.required, Validators.maxLength(120)])]
        });

        this.subscriptions.push(this.form.controls.message.valueChanges.subscribe(data => {
            this.messageLength = data.length;
        }));

        this.clientId = this.route.parent.snapshot.params['clientId'];
        this.subscriptions.push(this.route
            .params
            .subscribe(params => {
                this.consumerId = params['consumerId'];
            }));

        this.subscriptions.push(this.route
            .queryParams
            .subscribe(queryParams => {
                if (+queryParams['messagePage']) {
                    this.screenPage = +queryParams['messagePage'];
                }

            }));

        this.isSearching = true;
        this.consumerInteractionService.getDeviceScreenMessageHistory(this.clientId, this.consumerId, this.screenPage, this.pageSize).then((messageRes) => {
            this.screenMessages = messageRes.items;
            this.totalPages = messageRes.totalPages;
            this.pageSize = messageRes.pageSize;
            this.isSearching = false;
        })

        this.consumerInteractionService.getDeviceScreenMessageTemplates(this.clientId).then((messageTemplatesRes) => {
            this.messageTemplates = messageTemplatesRes;
            this.selectedTemplateMessage = this.messageTemplates[0].messageText;
            this.messageLength = this.selectedTemplateMessage.length;
            this.form.controls.message.setValue(this.selectedTemplateMessage);
        })

        this.consumerInteractionService.GetConsumerInteraction(this.clientId, this.consumerId, this.interactionType).then((interactionRes) => {
            let interaction = interactionRes as ConsumerInteractionModel[];
            if (!interaction) {
                this.showProgress = false;
            } else {
                this.showProgress = true;
                this.progress = getInteractionType(interaction);
            }

        }).catch((error) => {
            this.progress = ConsumerInteractionProgressType.Error;
            this.showProgress = false;
        })
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
    }

    onClickInfo() {

        swal({
            title: 'Screen messages',
            html: '<p style="text-align: left;" >Screen messages are a useful way of popping up a message on the PAYG device installed in the customers property. This is useful for notifications of tariff change, heat network outages and festive greetings.</p>',
            confirmButtonText: 'Close',
            confirmButtonColor: '#00539f'
        });

    }

    pageSelected(page: number) {
        this.screenPage = page;
        this.getPage(page);
    }

    getPage(page: number) {
        let queryParams: any = JSON.parse(JSON.stringify(this.route.snapshot.queryParams)) as any;
        queryParams.screenPage = page;

        this.consumerInteractionService.getDeviceScreenMessageHistory(this.clientId, this.consumerId, this.screenPage, this.pageSize).then((messageRes) => {
            var result = messageRes;

            if (result && result.items.length > 0) {
                this.screenMessages = result.items;
                this.pageSize = result.pageSize;
                this.totalPages = result.totalPages;
            }
        })
    }

    onInfoClick() {
        swal({
            title: 'Screen Messages',
            html: '<p style="text-align: left;">This widget shows the current balance of the consumer account.</p>\
             <p style="text-align: left;">Typically a PAYG consumer will have an account balance of zero, however, if debt has been applied then the account balance will show this. The PAYG unit has a separate balance known as the credit status, see the credit status widget.</p>\
             <p style="text-align: left;"> Switch2 is usually instructed to collect arrears at 20% of each top-up. In this scenario 20% of each payment would contribute to reducing the debt and the remaining 80% would be sent to the PAYG unit.</p>\
             <p style="text-align: left;" >Billing consumers will show as either in debt, zero balance or in credit.</p>\
             <p style="text-align: left;" >On expanding this widget you can see the line-by-line history of the account, and the resulting balance after each transaction made.</p>',
            confirmButtonText: 'Close',
            confirmButtonColor: '#00539f'
        });
    }

    onChange(value) {
        let templateItem = this.messageTemplates.find(x => x.messageName == value);
        if (templateItem) {
            this.selectedTemplateMessage = templateItem.messageText;
            this.messageLength = this.selectedTemplateMessage.length;
            this.form.controls.message.setValue(this.selectedTemplateMessage);
        }
    }

    toggleEditMessage() {
        this.editMessageToggle = !this.editMessageToggle;

        if (this.editMessageToggle) {
            this.elRef.nativeElement.parentElement.classList.add('module__edit');
        } else {
            this.elRef.nativeElement.parentElement.classList.remove('module__edit');
        }
    }

    onSubmit(form: UntypedFormGroup) {
        if (!form.valid || !form.dirty) return;
        if (form.controls.message.valid) {

            this.consumerInteractionService.PostDeviceScreenMessage(this.clientId, this.consumerId, { message: form.controls.message.value }).then((messageRes) => {
                this.consumerInteractionService.GetConsumerInteraction(this.clientId, this.consumerId, this.interactionType).then((interactionRes) => {
                    let interaction = interactionRes as ConsumerInteractionModel[];

                    if (!interaction) {
                        this.showProgress = false;
                    } else {
                        this.showProgress = true;
                        this.progress = getInteractionType(interaction);
                        this.toggleEditMessage();
                    }

                }).catch((error) => {
                    this.progress = ConsumerInteractionProgressType.Error;
                    this.showProgress = false;
                })
            });
        }
    }
}
